import { Circle as CircleStyle, Fill, Icon, Style, Stroke } from 'ol/style';
import { ColorNames, colors } from '@/compositions/map/utils/data';
import { mapCache } from '@/compositions/map/marker-cache';
import Point from 'ol/geom/Point';

export const markerSizes = {
  DEFAULT: 'DEFAULT',
  MINI: 'MINI',
  POINT: 'POINT'
};

const backgroundStyleSizes = {
  DEFAULT: {
    alarmed: {
      anchor: [0.5, 130]
    },
    normal: {
      anchor: [0.5, 1]
    },
    scale: 0.5
  },
  MINI: {
    alarmed: {
      anchor: [0.5, 130]
    },
    normal: {
      anchor: [0.5, 1]
    },
    scale: 0.333
  },
  POINT: {
    scale: 0.222,
    alarmed: {
      anchor: [0.5, 130]
    },
    normal: {
      anchor: [0.5, 1]
    }
  }
};

const iconStyleSizes = {
  DEFAULT: {
    alarmed: {
      anchor: {
        x: 0.5,
        y: 59
      }
    },
    normal: {
      anchor: {
        x: 0.5,
        y: 46
      }
    },
    scale: 24
  },
  MINI: {
    alarmed: {
      anchor: {
        x: 0.5,
        y: 40
      }
    },
    normal: {
      anchor: {
        x: 0.5,
        y: 30
      }
    },
    scale: 16
  },
  POINT: {
    scale: 0.222
  }
};

export const getMarkerAlarmStyle = (selected, scale) => {
  const key = `alarmed:background:${selected}`;
  if (mapCache.has(key)) {
    return mapCache.get(key);
  }
  const style = new Style({
    image: selected
      ? new Icon({
          src: require('@/assets/pin-alarmed.png'),
          color: colors[ColorNames.red],
          anchor: [0.5, 1],
          anchorXUnits: 'fraction',
          anchorYUnits: 'fraction',
          scale
        })
      : new Icon({
          src: require('@/assets/circle-alarmed.png'),
          color: colors[ColorNames.red],
          scale
        })
  });
  mapCache.set(key, style);
  return style;
};

export const getMarkerBackgroundStyle = (
  colorName,
  selected,
  alarmed,
  size
) => {
  const key = `color:${colorName}:selected:${selected}:alarmed:${alarmed}`;
  const sizeParams = backgroundStyleSizes[size];
  if (mapCache.has(key)) {
    return mapCache.get(key);
  }
  const resultStyle = [];
  if (alarmed) {
    resultStyle.push(getMarkerAlarmStyle(selected, sizeParams.scale));
  }
  if (selected) {
    if (alarmed) {
      resultStyle.push(
        new Style({
          image: new Icon({
            src: require('@/assets/circle.png'),
            color: colors[colorName] || colors[ColorNames.default],
            scale: sizeParams.scale,
            anchor: sizeParams.alarmed.anchor,
            anchorXUnits: 'fraction',
            anchorYUnits: 'pixels'
          })
        })
      );
    } else {
      resultStyle.push(
        new Style({
          image: new Icon({
            src: require('@/assets/pin.png'),
            color: colors[colorName] || colors[ColorNames.default],
            scale: sizeParams.scale,
            anchor: sizeParams.normal.anchor,
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction'
          })
        })
      );
    }
  } else {
    resultStyle.push(
      new Style({
        image: new Icon({
          src: require('@/assets/circle.png'),
          color: colors[colorName] || colors[ColorNames.default],
          scale: sizeParams.scale
        })
      })
    );
  }
  mapCache.set(key, resultStyle);
  return resultStyle;
};

export const getMarkerIconStyle = (icon, selected, alarmed, size) => {
  if (size === markerSizes.POINT || !icon) return null;

  const key = `icon:${icon}:selected:${selected}:alarmed:${alarmed}:${size}`;
  const sizeParams = iconStyleSizes[size];

  if (mapCache.has(key)) {
    return mapCache.get(key);
  }
  let style;
  if (!selected) {
    const image = new Icon({
      src: icon
    });
    let origImage = image.getImage();
    if (!origImage.width) {
      origImage.addEventListener(
        'load',
        function() {
          image.setScale([
            sizeParams.scale / origImage.width,
            sizeParams.scale / origImage.height
          ]);
        },
        { once: true }
      );
    } else {
      image.setScale([
        sizeParams.scale / origImage.width,
        sizeParams.scale / origImage.height
      ]);
    }
    style = new Style({
      image
    });
  } else {
    if (alarmed) {
      const image = new Icon({
        src: icon,
        anchorXUnits: 'fraction',
        anchorYUnits: 'pixels',
        anchor: [sizeParams.alarmed.anchor.x, sizeParams.alarmed.anchor.y]
      });
      let origImage = image.getImage();
      if (!origImage.width) {
        // TODO needs to be refactor
        origImage.addEventListener(
          'load',
          function() {
            image.setScale([
              sizeParams.scale / origImage.width,
              sizeParams.scale / origImage.height
            ]);
            image.setAnchor([
              sizeParams.alarmed.anchor.x,
              sizeParams.alarmed.anchor.y /
                (sizeParams.scale / origImage.height)
            ]);
          },
          { once: true }
        );
      } else {
        image.setScale([
          sizeParams.scale / origImage.width,
          sizeParams.scale / origImage.height
        ]);
        image.setAnchor([
          sizeParams.alarmed.anchor.x,
          sizeParams.alarmed.anchor.y / (sizeParams.scale / origImage.height)
        ]);
      }
      style = new Style({
        image
      });
    } else {
      const image = new Icon({
        src: icon,
        anchorXUnits: 'fraction',
        anchorYUnits: 'pixels',
        anchor: [sizeParams.normal.anchor.x, sizeParams.normal.anchor.y]
      });
      let origImage = image.getImage();
      if (!origImage.width) {
        origImage.addEventListener(
          'load',
          function() {
            image.setScale([
              sizeParams.scale / origImage.width,
              sizeParams.scale / origImage.height
            ]);
            image.setAnchor([
              sizeParams.normal.anchor.x,
              sizeParams.normal.anchor.y / (sizeParams.scale / origImage.height)
            ]);
          },
          { once: true }
        );
      } else {
        image.setScale([
          sizeParams.scale / origImage.width,
          sizeParams.scale / origImage.height
        ]);
        image.setAnchor([
          sizeParams.normal.anchor.x,
          sizeParams.normal.anchor.y / (sizeParams.scale / origImage.height)
        ]);
      }
      style = new Style({
        image
      });
    }
  }
  mapCache.set(key, style);
  return style;
};

export const getPointStyle = () => {
  return new Style({
    image: new CircleStyle({
      radius: 3,
      fill: new Fill({
        color: '#f00'
      })
    }),
    geometry: function(feature) {
      const coordinates = feature.getGeometry().getCoordinates();
      return new Point(coordinates);
    }
  });
};

export const getCirclePointStyle = (radius, resolution, colorName) => {
  const color = colors[colorName] || colors[ColorNames.default];
  const calculatedRadius = radius / resolution;
  const key = `circle_style_color:${colorName}:radius:${calculatedRadius}`;

  if (mapCache.has(key)) {
    return mapCache.get(key);
  }

  const style = new Style({
    image: new CircleStyle({
      radius: calculatedRadius,
      fill: new Fill({
        color: hexToRgbaArray(color, 0.5) // полупрозрачный красный
      }),
      stroke: new Stroke({
        color, // такой же цвет, но непрозрачный
        width: 2
      })
    })
  });

  mapCache.set(key, style);

  return style;
};

export function hexToRgbaArray(hex, alpha = 1.0) {
  // Удаляем решётку, если есть
  let c = hex.substring(1).split('');

  // Если короткая запись (#RGB) — преобразуем в полную (#RRGGBB)
  if (c.length === 3) {
    c = [c[0], c[0], c[1], c[1], c[2], c[2]];
  }

  // Склеиваем обратно в строку и парсим числа
  const fullHex = c.join('');
  const r = parseInt(fullHex.substring(0, 2), 16);
  const g = parseInt(fullHex.substring(2, 4), 16);
  const b = parseInt(fullHex.substring(4, 6), 16);

  return [r, g, b, alpha];
}
